import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { scrollToSection } from "@/lib/api";
import { useSiteContent } from "@/lib/content";
import { AnimatePresence, motion } from "framer-motion";

export const Navbar = ({ onEnquire }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const c = useSiteContent();
  const LINKS = (c.nav_links || []).filter((l) => l.visible !== false);

  const go = (target) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: target } });
    } else {
      scrollToSection(target);
    }
  };

  const enquire = () => {
    setOpen(false);
    if (onEnquire) onEnquire();
    else navigate("/", { state: { scrollTo: "#contact" } });
  };

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[var(--ekal-bg)]/80 border-b border-white/10"
    >
      <div className="px-4 sm:px-8 lg:px-16 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand" className="flex items-center gap-2.5 group">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--ekal-accent)] opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[var(--ekal-accent)]" />
          </span>
          <span className="font-heading font-extrabold text-xl tracking-tight uppercase">
            Ekal <span className="text-[var(--ekal-accent)]">Sports</span> Media
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.target}
              data-testid={l.testid}
              onClick={() => go(l.target)}
              className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400 hover:text-white transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <button
            data-testid="nav-enquire-btn"
            onClick={enquire}
            className="bg-[var(--ekal-accent)] hover:bg-red-600 text-white text-xs font-mono uppercase tracking-[0.18em] px-5 py-2.5 transition-colors"
          >
            Enquire
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-slate-200"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-[var(--ekal-bg)]/95 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.target}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => go(l.target)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: 0.06 * i, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  className="text-left font-heading font-bold uppercase text-2xl tracking-tight text-slate-200 hover:text-[var(--ekal-accent)] transition-colors"
                >
                  <span className="font-mono text-[10px] text-[var(--ekal-accent)] mr-3">0{i + 1}</span>
                  {l.label}
                </motion.button>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.06 * LINKS.length + 0.1, duration: 0.3 }}
                className="flex items-center gap-4 pt-2"
              >
                <button
                  data-testid="nav-enquire-btn-mobile"
                  onClick={enquire}
                  className="bg-[var(--ekal-accent)] text-white text-xs font-mono uppercase tracking-[0.18em] px-5 py-2.5"
                >
                  Enquire
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
